import React from "react";
import { GridView, Home, Insights, MultipleStop, ShowChart, SocialDistance, Straighten } from "@mui/icons-material";
import { Alert, Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { formatCurrency } from "fond/utils/currency";
import { formatInteger, formatNumber } from "fond/utils/number";

import { InteractiveBomView } from "../constants";

import { AccentBar, IconBadge, SummaryCard, SummaryColumn } from "./BomSummary.styles";

interface BomSummaryProps {
  totalCost: number;
  totalPath: number | undefined;
  serviceLocationsCount: number | undefined;
  unitLabel: string;
  view: InteractiveBomView;
  isPlannerProject: boolean;
  currency?: string;
  onViewChange(view: InteractiveBomView): void;
}

const BomSummary: React.FC<BomSummaryProps> = ({
  totalCost,
  totalPath,
  serviceLocationsCount,
  unitLabel,
  view,
  isPlannerProject,
  currency,
  onViewChange,
}: BomSummaryProps) => {
  const avgCostPerLinearUnit = totalPath !== undefined && totalPath > 0 ? totalCost / totalPath : undefined;
  const avgCostPerServiceLocation = serviceLocationsCount !== undefined && serviceLocationsCount > 0 ? totalCost / serviceLocationsCount : undefined;
  const avgLinearDistancePerServiceLocation =
    totalPath !== undefined && serviceLocationsCount !== undefined && serviceLocationsCount > 0 ? totalPath / serviceLocationsCount : undefined;

  return (
    <Box mb={2}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_event, value: InteractiveBomView | null) => value !== null && onViewChange(value)}
          sx={{ backgroundColor: "common.white", boxShadow: 1 }}
        >
          <ToggleButton value="analytics">
            <Insights fontSize="small" sx={{ mr: 0.5 }} />
            Analytics
          </ToggleButton>
          <ToggleButton value="grid">
            <GridView fontSize="small" sx={{ mr: 0.5 }} />
            Grid
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <SummaryCard>
        <AccentBar />
        <SummaryColumn>
          <Typography variant="body3" color="text.secondary" fontWeight={700} letterSpacing={0.5} textTransform="uppercase">
            Total construction cost
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {formatCurrency(totalCost, { forceDecimals: true, currency })}
          </Typography>
        </SummaryColumn>
        <SummaryColumn>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconBadge>
              <Home />
            </IconBadge>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Service locations
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatInteger(serviceLocationsCount)}
              </Typography>
            </Box>
          </Box>
        </SummaryColumn>

        <SummaryColumn>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconBadge>
              <ShowChart />
            </IconBadge>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total path
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatInteger(totalPath)} {unitLabel}
              </Typography>
            </Box>
          </Box>
        </SummaryColumn>
        <SummaryColumn>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconBadge>
              <MultipleStop />
            </IconBadge>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Avg. cost / service location
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatCurrency(avgCostPerServiceLocation, { forceDecimals: true, currency })}
              </Typography>
            </Box>
          </Box>
        </SummaryColumn>
        <SummaryColumn>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconBadge>
              <Straighten />
            </IconBadge>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Avg. cost / linear {unitLabel}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatCurrency(avgCostPerLinearUnit, { forceDecimals: true, currency })}
              </Typography>
            </Box>
          </Box>
        </SummaryColumn>
        <SummaryColumn>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconBadge>
              <SocialDistance />
            </IconBadge>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Avg. linear {unitLabel} / service location
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatNumber(avgLinearDistancePerServiceLocation, 2)}
              </Typography>
            </Box>
          </Box>
        </SummaryColumn>
      </SummaryCard>
      {isPlannerProject && (
        <Alert severity="info" sx={{ mt: 2 }}>
          The materials and costs can be configured in the Architecture Panel. Costs and quantities reflect the current design.{" "}
          <a href="https://fondhelp.biarrinetworks.com/configuring-your-bom" target="_blank" rel="noopener noreferrer">
            Learn how to configure a BOM
          </a>
          .
        </Alert>
      )}
    </Box>
  );
};

export default BomSummary;
